"use client";

import { useEffect, useMemo, useSyncExternalStore } from "react";

export interface NavigationAccessSnapshot {
  recruiterName: string | null;
  submissions: boolean;
  jobs: boolean;
  reports: boolean;
  sola: boolean;
}

const STORAGE_KEY = "floats:navigation-access";
const CHANGE_EVENT = "floats:navigation-access-change";

const subscribe = (onStoreChange: () => void) => {
  window.addEventListener("storage", onStoreChange);
  window.addEventListener(CHANGE_EVENT, onStoreChange);
  return () => {
    window.removeEventListener("storage", onStoreChange);
    window.removeEventListener(CHANGE_EVENT, onStoreChange);
  };
};

const getSnapshot = () => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

export default function useNavigationAccessSnapshot(
  liveSnapshot: NavigationAccessSnapshot | null
) {
  const liveValue = liveSnapshot ? JSON.stringify(liveSnapshot) : null;
  const storedValue = useSyncExternalStore(subscribe, getSnapshot, () => null);
  const storedSnapshot = useMemo(() => {
    if (!storedValue) return null;
    try {
      return JSON.parse(storedValue) as NavigationAccessSnapshot;
    } catch {
      return null;
    }
  }, [storedValue]);

  useEffect(() => {
    if (!liveValue) return;
    try {
      window.sessionStorage.setItem(STORAGE_KEY, liveValue);
      window.dispatchEvent(new Event(CHANGE_EVENT));
    } catch {
      // Live permissions remain the source of truth when storage is blocked.
    }
  }, [liveValue]);

  return liveSnapshot ?? storedSnapshot;
}
